/** Constantes partagées entre l'API et le front. Aucune dépendance runtime. */

import {
  FamilleArtisanat,
  ModeOrdonnanceur,
  OrigineConfigCollecte,
  StatutConfigCollecte,
} from '../enums/index.js';

// ── Routage HTTP ───────────────────────────────────────────────────────────

export const API_PREFIX = 'api';
export const API_VERSION = 'v1';
/** Préfixe complet de toutes les routes : `/api/v1`. */
export const API_BASE_PATH = `/${API_PREFIX}/${API_VERSION}`;

// ── Pagination ─────────────────────────────────────────────────────────────

export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

// ── Cookies ────────────────────────────────────────────────────────────────

/**
 * Noms des cookies d'authentification. Le refresh est restreint à la route de
 * renouvellement : il n'accompagne aucune autre requête.
 */
export const COOKIE = {
  ACCESS: 'mirsad_access',
  REFRESH: 'mirsad_refresh',
} as const;

/** Chemin sur lequel le cookie de rafraîchissement est émis. */
export const REFRESH_COOKIE_PATH = `${API_BASE_PATH}/auth/refresh`;

// ── Politique de mot de passe (valeurs par défaut, surchargées par AppSettings) ──

export const PASSWORD_POLICY = {
  MIN_LENGTH: 12,
  HISTORY_SIZE: 5,
  MAX_FAILED_ATTEMPTS: 5,
  LOCK_DURATION_MINUTES: 15,
} as const;

// ── Téléversements ─────────────────────────────────────────────────────────

export const UPLOAD = {
  DEFAULT_MAX_SIZE_MB: 10,
  MIME_IMAGES: ['image/jpeg', 'image/png', 'image/webp', 'image/avif'] as const,
  MIME_DOCUMENTS: ['application/pdf'] as const,
} as const;

// ── Rapports avancés ───────────────────────────────────────────────────────

/**
 * Polices offertes aux modèles de rapport. L'éditeur les propose, l'assistant
 * y replie les polices relevées dans un rapport d'exemple, et les exports
 * savent toutes les rendre : Word telles quelles, PDF par famille standard.
 */
export const POLICES_MODELE = ['Calibri', 'Georgia', 'Inter', 'Roboto'] as const;
export type PoliceModele = (typeof POLICES_MODELE)[number];

/**
 * Rapport existant dont l'assistant déduit un modèle : formats lus et taille
 * maximale. Le fichier est lu en mémoire puis oublié, jamais conservé.
 */
export const DOCUMENT_EXEMPLE = {
  MAX_SIZE_MB: 40,
  EXTENSIONS: ['.pdf', '.docx', '.pptx'] as const,
} as const;

// ── Seuils métier ──────────────────────────────────────────────────────────

/**
 * Un avis client vaut une transaction réelle : c'est la seule mesure de ventes
 * disponible au niveau article. En deçà de ce seuil, une moyenne n'a pas de sens.
 */
export const VENTES_MIN_POUR_MOYENNE = 3;

/** Au-delà, un terme est considéré comme sur-représenté chez les articles qui vendent. */
export const SEUIL_SUR_REPRESENTATION = 1.5;

/** Dimension des plongements vectoriels (défaut du modèle retenu). */
export const EMBEDDING_DIMENSIONS = 1024;

// ── Enrichissements de collecte ────────────────────────────────────────────

/**
 * Postes d'enrichissement d'une collecte, décrits une seule fois : l'API valide
 * les clés contre cette liste, en tire le coût par article, et le front en
 * dérive les cases à cocher. Un libellé inventé d'un côté était auparavant
 * refusé par l'autre sans que rien ne le signale à la compilation.
 *
 * `coutParArticle` distingue les deux régimes de facturation : la plupart des
 * postes arrivent par lots de cent articles et ne coûtent rien de plus, tandis
 * que variantes et attributs consomment un appel **par article** — c'est ce
 * déséquilibre qui décide si une collecte tient dans la fenêtre de 24 h.
 */
export interface Enrichissement {
  readonly cle: string;
  readonly libelle: string;
  /** Coût affiché, exprimé dans l'unité qui compte pour ce poste. */
  readonly cout: string;
  /** Appels consommés par article ; 0 quand le poste arrive par lots. */
  readonly coutParArticle: number;
  /** Coché d'emblée dans le formulaire de collecte. */
  readonly defaut: boolean;
  /** Exige une autorisation de la source : refusé sans jeton. */
  readonly autorisation?: boolean;
}

export const ENRICHISSEMENTS: readonly Enrichissement[] = [
  { cle: 'images', libelle: 'Photos', cout: 'inclus', coutParArticle: 0, defaut: true },
  { cle: 'boutique', libelle: 'Boutique', cout: 'inclus', coutParArticle: 0, defaut: true },
  {
    cle: 'taxonomie',
    libelle: 'Catégories',
    cout: '1 appel par mois',
    coutParArticle: 0,
    defaut: true,
  },
  {
    cle: 'avis',
    libelle: 'Avis (= ventes mesurées)',
    cout: '1 / boutique, + 1 / 100 avis nouveaux',
    coutParArticle: 0,
    defaut: true,
  },
  {
    cle: 'sections',
    libelle: 'Rayons et retours',
    cout: '2 / boutique',
    coutParArticle: 0,
    defaut: false,
  },
  {
    cle: 'attributs',
    libelle: 'Attributs structurés',
    cout: '1 / article',
    coutParArticle: 1,
    defaut: false,
  },
  {
    cle: 'variantes',
    libelle: 'Variantes et prix par option',
    cout: '1 / article',
    coutParArticle: 1,
    defaut: false,
    /** Exige une autorisation de la source : refusé sans jeton. */
    autorisation: true,
  },
  {
    cle: 'articles_vendus',
    libelle: 'Articles vendus découverts par les avis',
    cout: '1 / 100',
    coutParArticle: 0,
    defaut: false,
  },
];

/** Les seules clés acceptées dans `definition.enrichissements`. */
export const CLES_ENRICHISSEMENT: readonly string[] = ENRICHISSEMENTS.map((e) => e.cle);

/** Coût en appels par article, pour les seuls postes facturés à l'unité. */
export const COUT_ENRICHISSEMENT_PAR_ARTICLE: Record<string, number> = Object.fromEntries(
  ENRICHISSEMENTS.filter((e) => e.coutParArticle > 0).map((e) => [e.cle, e.coutParArticle]),
);

// ── Configurations de collecte : statut, origine, requêtes par tag ─────────

export const STATUT_CONFIG_COLLECTE_LABELS: Record<StatutConfigCollecte, string> = {
  [StatutConfigCollecte.EN_PAUSE]: 'En pause',
  [StatutConfigCollecte.ACTIVE]: 'Active',
  [StatutConfigCollecte.ARCHIVEE]: 'Archivée',
};

export const ORIGINE_CONFIG_COLLECTE_LABELS: Record<OrigineConfigCollecte, string> = {
  [OrigineConfigCollecte.MANUELLE]: 'Manuelle',
  [OrigineConfigCollecte.TAGS]: 'Générée depuis les tags',
  [OrigineConfigCollecte.CATEGORIE]: 'Générée par catégorie',
  [OrigineConfigCollecte.BOUTIQUE]: 'Générée par boutique',
};

/**
 * Définition appliquée aux requêtes générées depuis les tags quand
 * l'administration n'en a pas fixé une. Les mots-clés n'y figurent pas :
 * chaque requête porte exactement son tag.
 *
 * Boutiques marocaines seulement, et les seuls enrichissements facturés par
 * centaine : une requête en pause activée par inadvertance ne doit pas
 * pouvoir vider le quota.
 */
export const DEFINITION_TAGS_DEFAUT = {
  filtres: { paysBoutique: 'Morocco', paysExiges: ['MA'], deviseReference: 'EUR' },
  volume: { maxParMotCle: 200, tri: 'score', ordre: 'desc' },
  enrichissements: ['images', 'boutique', 'taxonomie', 'avis'],
} as const;

/** Cadence d'une requête générée au moment de son activation. */
export const TAGS_CRON_DEFAUT = '0 2 * * 1';
export const TAGS_RAFRAICHIR_APRES_JOURS_DEFAUT = 7;
/** Nombre d'articles à partir duquel un couple tag × famille devient une requête. */
export const TAGS_SEUIL_ARTICLES_DEFAUT = 20;

// ── Ordonnanceur ───────────────────────────────────────────────────────────

export const MODE_ORDONNANCEUR_LABELS: Record<ModeOrdonnanceur, string> = {
  [ModeOrdonnanceur.CADENCE]: 'Cadence par requête',
  [ModeOrdonnanceur.BOUCLE]: 'Boucle',
  [ModeOrdonnanceur.COUVERTURE]: 'Couverture des familles',
};

export const MODE_ORDONNANCEUR_DESCRIPTIONS: Record<ModeOrdonnanceur, string> = {
  [ModeOrdonnanceur.CADENCE]:
    'Chaque requête active dotée d’une cadence est relancée quand son délai de rafraîchissement est écoulé.',
  [ModeOrdonnanceur.BOUCLE]:
    'Parcourt les requêtes actives et les requêtes générées en pause, la moins récemment collectée d’abord, et recommence.',
  [ModeOrdonnanceur.COUVERTURE]:
    'Répartit la collecte entre les familles à l’inverse de leur part du catalogue, d’après les articles nouveaux qu’elle leur rapporte : les petites familles passent plus souvent. Chaque requête moissonne en profondeur ; les variantes sont rattrapées en fin de tour.',
};

/** Part du quota journalier réservée aux lancements automatiques (%). */
export const ORDONNANCEUR_PART_QUOTA_DEFAUT = 80;
/** En couverture : part minimale des lancements garantie à la plus grosse famille (%). */
export const ORDONNANCEUR_PLANCHER_FAMILLE_DEFAUT = 5;
/** Sous ce budget, l'ordonnanceur ne lance rien : le pipeline s'arrêterait avant la première page. */
export const ORDONNANCEUR_BUDGET_MIN = 50;

// ── Boutiques : segments du paysage ────────────────────────────────────────

/** Segments du paysage des boutiques, dans l'ordre où la page les présente. */
export const SEGMENTS_BOUTIQUE = ['locomotives', 'montantes', 'sommeil', 'approfondir'] as const;
export type SegmentBoutique = (typeof SEGMENTS_BOUTIQUE)[number];

/**
 * Seuils des segments de boutiques, réglables dans les Paramètres. Tant
 * qu'aucun réglage n'est enregistré, les valeurs par défaut s'appliquent.
 */
export interface SeuilsSegmentsBoutiques {
  /** Locomotives : part des ventes mesurées qu'elles font ensemble (%). */
  partLocomotives: number;
  /** Montantes : âge maximal depuis l'ouverture de la boutique (mois). */
  moisMontante: number;
  /** En sommeil : ventes mesurées minimales, et aucune sur 90 jours. */
  ventesSommeil: number;
  /** À approfondir : annonces en ligne minimales d'une boutique marocaine… */
  annoncesApprofondir: number;
  /** …dont le relevé couvre moins que cette part (%). */
  couvertureApprofondir: number;
}

export const SEUILS_SEGMENTS_BOUTIQUES_DEFAUT: SeuilsSegmentsBoutiques = {
  partLocomotives: 50,
  moisMontante: 24,
  ventesSommeil: 20,
  annoncesApprofondir: 100,
  couvertureApprofondir: 5,
};

// ── Pays de boutique ───────────────────────────────────────────────────────

/**
 * Pays d'une boutique — liste ISO 3166-1 alpha-2 complète.
 *
 * Deux usages distincts, d'où les deux noms portés par chaque entrée :
 *
 * - `nomSource` filtre **à la source**, qui n'accepte qu'un seul pays par
 *   recherche et n'en reconnaît que le nom anglais. Un code ISO n'y provoque
 *   aucune erreur : il renvoie zéro résultat (mesuré — « US » ramène 0 annonce
 *   quand « United States » en ramène 31 233).
 * - `code` filtre **après coup**, sur le pays que la boutique déclare. C'est
 *   notre filtre à nous : il accepte plusieurs pays, et c'est ainsi qu'on vise
 *   plusieurs marchés en une seule recherche.
 *
 * Libellés et noms anglais sont produits par `Intl.DisplayNames`, pas saisis à
 * la main : 249 pays recopiés seraient 249 occasions de faute de frappe.
 */
export interface PaysBoutique {
  /** Code ISO 3166-1 alpha-2, tel que la source le déclare sur la boutique. */
  readonly code: string;
  readonly libelle: string;
  /**
   * Nom attendu par le paramètre de recherche de la source, en anglais.
   * Le code ISO n'y est pas reconnu : c'est le nom qui filtre.
   */
  readonly nomSource: string;
}

export const PAYS_BOUTIQUE: readonly PaysBoutique[] = [
  { code: 'AF', libelle: 'Afghanistan', nomSource: 'Afghanistan' },
  { code: 'ZA', libelle: 'Afrique du Sud', nomSource: 'South Africa' },
  { code: 'AL', libelle: 'Albanie', nomSource: 'Albania' },
  { code: 'DZ', libelle: 'Algérie', nomSource: 'Algeria' },
  { code: 'DE', libelle: 'Allemagne', nomSource: 'Germany' },
  { code: 'AD', libelle: 'Andorre', nomSource: 'Andorra' },
  { code: 'AO', libelle: 'Angola', nomSource: 'Angola' },
  { code: 'AI', libelle: 'Anguilla', nomSource: 'Anguilla' },
  { code: 'AQ', libelle: 'Antarctique', nomSource: 'Antarctica' },
  { code: 'AG', libelle: 'Antigua-et-Barbuda', nomSource: 'Antigua and Barbuda' },
  { code: 'SA', libelle: 'Arabie saoudite', nomSource: 'Saudi Arabia' },
  { code: 'AR', libelle: 'Argentine', nomSource: 'Argentina' },
  { code: 'AM', libelle: 'Arménie', nomSource: 'Armenia' },
  { code: 'AW', libelle: 'Aruba', nomSource: 'Aruba' },
  { code: 'AU', libelle: 'Australie', nomSource: 'Australia' },
  { code: 'AT', libelle: 'Autriche', nomSource: 'Austria' },
  { code: 'AZ', libelle: 'Azerbaïdjan', nomSource: 'Azerbaijan' },
  { code: 'BS', libelle: 'Bahamas', nomSource: 'Bahamas' },
  { code: 'BH', libelle: 'Bahreïn', nomSource: 'Bahrain' },
  { code: 'BD', libelle: 'Bangladesh', nomSource: 'Bangladesh' },
  { code: 'BB', libelle: 'Barbade', nomSource: 'Barbados' },
  { code: 'BE', libelle: 'Belgique', nomSource: 'Belgium' },
  { code: 'BZ', libelle: 'Belize', nomSource: 'Belize' },
  { code: 'BJ', libelle: 'Bénin', nomSource: 'Benin' },
  { code: 'BM', libelle: 'Bermudes', nomSource: 'Bermuda' },
  { code: 'BT', libelle: 'Bhoutan', nomSource: 'Bhutan' },
  { code: 'BY', libelle: 'Biélorussie', nomSource: 'Belarus' },
  { code: 'BO', libelle: 'Bolivie', nomSource: 'Bolivia' },
  { code: 'BA', libelle: 'Bosnie-Herzégovine', nomSource: 'Bosnia and Herzegovina' },
  { code: 'BW', libelle: 'Botswana', nomSource: 'Botswana' },
  { code: 'BR', libelle: 'Brésil', nomSource: 'Brazil' },
  { code: 'BN', libelle: 'Brunei', nomSource: 'Brunei' },
  { code: 'BG', libelle: 'Bulgarie', nomSource: 'Bulgaria' },
  { code: 'BF', libelle: 'Burkina Faso', nomSource: 'Burkina Faso' },
  { code: 'BI', libelle: 'Burundi', nomSource: 'Burundi' },
  { code: 'KH', libelle: 'Cambodge', nomSource: 'Cambodia' },
  { code: 'CM', libelle: 'Cameroun', nomSource: 'Cameroon' },
  { code: 'CA', libelle: 'Canada', nomSource: 'Canada' },
  { code: 'CV', libelle: 'Cap-Vert', nomSource: 'Cape Verde' },
  { code: 'CL', libelle: 'Chili', nomSource: 'Chile' },
  { code: 'CN', libelle: 'Chine', nomSource: 'China' },
  { code: 'CY', libelle: 'Chypre', nomSource: 'Cyprus' },
  { code: 'CO', libelle: 'Colombie', nomSource: 'Colombia' },
  { code: 'KM', libelle: 'Comores', nomSource: 'Comoros' },
  { code: 'CG', libelle: 'Congo-Brazzaville', nomSource: 'Congo - Brazzaville' },
  { code: 'CD', libelle: 'Congo-Kinshasa', nomSource: 'Congo - Kinshasa' },
  { code: 'KP', libelle: 'Corée du Nord', nomSource: 'North Korea' },
  { code: 'KR', libelle: 'Corée du Sud', nomSource: 'South Korea' },
  { code: 'CR', libelle: 'Costa Rica', nomSource: 'Costa Rica' },
  { code: 'CI', libelle: 'Côte d’Ivoire', nomSource: 'Côte d’Ivoire' },
  { code: 'HR', libelle: 'Croatie', nomSource: 'Croatia' },
  { code: 'CU', libelle: 'Cuba', nomSource: 'Cuba' },
  { code: 'CW', libelle: 'Curaçao', nomSource: 'Curaçao' },
  { code: 'DK', libelle: 'Danemark', nomSource: 'Denmark' },
  { code: 'DJ', libelle: 'Djibouti', nomSource: 'Djibouti' },
  { code: 'DM', libelle: 'Dominique', nomSource: 'Dominica' },
  { code: 'EG', libelle: 'Égypte', nomSource: 'Egypt' },
  { code: 'AE', libelle: 'Émirats arabes unis', nomSource: 'United Arab Emirates' },
  { code: 'EC', libelle: 'Équateur', nomSource: 'Ecuador' },
  { code: 'ER', libelle: 'Érythrée', nomSource: 'Eritrea' },
  { code: 'ES', libelle: 'Espagne', nomSource: 'Spain' },
  { code: 'EE', libelle: 'Estonie', nomSource: 'Estonia' },
  { code: 'SZ', libelle: 'Eswatini', nomSource: 'Eswatini' },
  { code: 'VA', libelle: 'État de la Cité du Vatican', nomSource: 'Vatican City' },
  { code: 'US', libelle: 'États-Unis', nomSource: 'United States' },
  { code: 'ET', libelle: 'Éthiopie', nomSource: 'Ethiopia' },
  { code: 'FJ', libelle: 'Fidji', nomSource: 'Fiji' },
  { code: 'FI', libelle: 'Finlande', nomSource: 'Finland' },
  { code: 'FR', libelle: 'France', nomSource: 'France' },
  { code: 'GA', libelle: 'Gabon', nomSource: 'Gabon' },
  { code: 'GM', libelle: 'Gambie', nomSource: 'Gambia' },
  { code: 'GE', libelle: 'Géorgie', nomSource: 'Georgia' },
  {
    code: 'GS',
    libelle: 'Géorgie du Sud-et-les Îles Sandwich du Sud',
    nomSource: 'South Georgia and South Sandwich Islands',
  },
  { code: 'GH', libelle: 'Ghana', nomSource: 'Ghana' },
  { code: 'GI', libelle: 'Gibraltar', nomSource: 'Gibraltar' },
  { code: 'GR', libelle: 'Grèce', nomSource: 'Greece' },
  { code: 'GD', libelle: 'Grenade', nomSource: 'Grenada' },
  { code: 'GL', libelle: 'Groenland', nomSource: 'Greenland' },
  { code: 'GP', libelle: 'Guadeloupe', nomSource: 'Guadeloupe' },
  { code: 'GU', libelle: 'Guam', nomSource: 'Guam' },
  { code: 'GT', libelle: 'Guatemala', nomSource: 'Guatemala' },
  { code: 'GG', libelle: 'Guernesey', nomSource: 'Guernsey' },
  { code: 'GN', libelle: 'Guinée', nomSource: 'Guinea' },
  { code: 'GQ', libelle: 'Guinée équatoriale', nomSource: 'Equatorial Guinea' },
  { code: 'GW', libelle: 'Guinée-Bissau', nomSource: 'Guinea-Bissau' },
  { code: 'GY', libelle: 'Guyana', nomSource: 'Guyana' },
  { code: 'GF', libelle: 'Guyane française', nomSource: 'French Guiana' },
  { code: 'HT', libelle: 'Haïti', nomSource: 'Haiti' },
  { code: 'HN', libelle: 'Honduras', nomSource: 'Honduras' },
  { code: 'HU', libelle: 'Hongrie', nomSource: 'Hungary' },
  { code: 'BV', libelle: 'Île Bouvet', nomSource: 'Bouvet Island' },
  { code: 'CX', libelle: 'Île Christmas', nomSource: 'Christmas Island' },
  { code: 'IM', libelle: 'Île de Man', nomSource: 'Isle of Man' },
  { code: 'NF', libelle: 'Île Norfolk', nomSource: 'Norfolk Island' },
  { code: 'AX', libelle: 'Îles Åland', nomSource: 'Åland Islands' },
  { code: 'KY', libelle: 'Îles Caïmans', nomSource: 'Cayman Islands' },
  { code: 'CC', libelle: 'Îles Cocos', nomSource: 'Cocos (Keeling) Islands' },
  { code: 'CK', libelle: 'Îles Cook', nomSource: 'Cook Islands' },
  { code: 'FO', libelle: 'Îles Féroé', nomSource: 'Faroe Islands' },
  { code: 'HM', libelle: 'Îles Heard-et-MacDonald', nomSource: 'Heard and McDonald Islands' },
  { code: 'FK', libelle: 'Îles Malouines', nomSource: 'Falkland Islands' },
  { code: 'MP', libelle: 'Îles Mariannes du Nord', nomSource: 'Northern Mariana Islands' },
  { code: 'MH', libelle: 'Îles Marshall', nomSource: 'Marshall Islands' },
  {
    code: 'UM',
    libelle: 'Îles mineures éloignées des États-Unis',
    nomSource: 'U.S. Outlying Islands',
  },
  { code: 'PN', libelle: 'Îles Pitcairn', nomSource: 'Pitcairn Islands' },
  { code: 'SB', libelle: 'Îles Salomon', nomSource: 'Solomon Islands' },
  { code: 'TC', libelle: 'Îles Turques-et-Caïques', nomSource: 'Turks and Caicos Islands' },
  { code: 'VG', libelle: 'Îles Vierges britanniques', nomSource: 'British Virgin Islands' },
  { code: 'VI', libelle: 'Îles Vierges des États-Unis', nomSource: 'U.S. Virgin Islands' },
  { code: 'IN', libelle: 'Inde', nomSource: 'India' },
  { code: 'ID', libelle: 'Indonésie', nomSource: 'Indonesia' },
  { code: 'IQ', libelle: 'Irak', nomSource: 'Iraq' },
  { code: 'IR', libelle: 'Iran', nomSource: 'Iran' },
  { code: 'IE', libelle: 'Irlande', nomSource: 'Ireland' },
  { code: 'IS', libelle: 'Islande', nomSource: 'Iceland' },
  { code: 'IL', libelle: 'Israël', nomSource: 'Israel' },
  { code: 'IT', libelle: 'Italie', nomSource: 'Italy' },
  { code: 'JM', libelle: 'Jamaïque', nomSource: 'Jamaica' },
  { code: 'JP', libelle: 'Japon', nomSource: 'Japan' },
  { code: 'JE', libelle: 'Jersey', nomSource: 'Jersey' },
  { code: 'JO', libelle: 'Jordanie', nomSource: 'Jordan' },
  { code: 'KZ', libelle: 'Kazakhstan', nomSource: 'Kazakhstan' },
  { code: 'KE', libelle: 'Kenya', nomSource: 'Kenya' },
  { code: 'KG', libelle: 'Kirghizstan', nomSource: 'Kyrgyzstan' },
  { code: 'KI', libelle: 'Kiribati', nomSource: 'Kiribati' },
  { code: 'KW', libelle: 'Koweït', nomSource: 'Kuwait' },
  { code: 'RE', libelle: 'La Réunion', nomSource: 'Réunion' },
  { code: 'LA', libelle: 'Laos', nomSource: 'Laos' },
  { code: 'LS', libelle: 'Lesotho', nomSource: 'Lesotho' },
  { code: 'LV', libelle: 'Lettonie', nomSource: 'Latvia' },
  { code: 'LB', libelle: 'Liban', nomSource: 'Lebanon' },
  { code: 'LR', libelle: 'Liberia', nomSource: 'Liberia' },
  { code: 'LY', libelle: 'Libye', nomSource: 'Libya' },
  { code: 'LI', libelle: 'Liechtenstein', nomSource: 'Liechtenstein' },
  { code: 'LT', libelle: 'Lituanie', nomSource: 'Lithuania' },
  { code: 'LU', libelle: 'Luxembourg', nomSource: 'Luxembourg' },
  { code: 'MK', libelle: 'Macédoine du Nord', nomSource: 'North Macedonia' },
  { code: 'MG', libelle: 'Madagascar', nomSource: 'Madagascar' },
  { code: 'MY', libelle: 'Malaisie', nomSource: 'Malaysia' },
  { code: 'MW', libelle: 'Malawi', nomSource: 'Malawi' },
  { code: 'MV', libelle: 'Maldives', nomSource: 'Maldives' },
  { code: 'ML', libelle: 'Mali', nomSource: 'Mali' },
  { code: 'MT', libelle: 'Malte', nomSource: 'Malta' },
  { code: 'MA', libelle: 'Maroc', nomSource: 'Morocco' },
  { code: 'MQ', libelle: 'Martinique', nomSource: 'Martinique' },
  { code: 'MU', libelle: 'Maurice', nomSource: 'Mauritius' },
  { code: 'MR', libelle: 'Mauritanie', nomSource: 'Mauritania' },
  { code: 'YT', libelle: 'Mayotte', nomSource: 'Mayotte' },
  { code: 'MX', libelle: 'Mexique', nomSource: 'Mexico' },
  { code: 'FM', libelle: 'Micronésie', nomSource: 'Micronesia' },
  { code: 'MD', libelle: 'Moldavie', nomSource: 'Moldova' },
  { code: 'MC', libelle: 'Monaco', nomSource: 'Monaco' },
  { code: 'MN', libelle: 'Mongolie', nomSource: 'Mongolia' },
  { code: 'ME', libelle: 'Monténégro', nomSource: 'Montenegro' },
  { code: 'MS', libelle: 'Montserrat', nomSource: 'Montserrat' },
  { code: 'MZ', libelle: 'Mozambique', nomSource: 'Mozambique' },
  { code: 'MM', libelle: 'Myanmar (Birmanie)', nomSource: 'Myanmar (Burma)' },
  { code: 'NA', libelle: 'Namibie', nomSource: 'Namibia' },
  { code: 'NR', libelle: 'Nauru', nomSource: 'Nauru' },
  { code: 'NP', libelle: 'Népal', nomSource: 'Nepal' },
  { code: 'NI', libelle: 'Nicaragua', nomSource: 'Nicaragua' },
  { code: 'NE', libelle: 'Niger', nomSource: 'Niger' },
  { code: 'NG', libelle: 'Nigeria', nomSource: 'Nigeria' },
  { code: 'NU', libelle: 'Niue', nomSource: 'Niue' },
  { code: 'NO', libelle: 'Norvège', nomSource: 'Norway' },
  { code: 'NC', libelle: 'Nouvelle-Calédonie', nomSource: 'New Caledonia' },
  { code: 'NZ', libelle: 'Nouvelle-Zélande', nomSource: 'New Zealand' },
  { code: 'OM', libelle: 'Oman', nomSource: 'Oman' },
  { code: 'UG', libelle: 'Ouganda', nomSource: 'Uganda' },
  { code: 'UZ', libelle: 'Ouzbékistan', nomSource: 'Uzbekistan' },
  { code: 'PK', libelle: 'Pakistan', nomSource: 'Pakistan' },
  { code: 'PW', libelle: 'Palaos', nomSource: 'Palau' },
  { code: 'PA', libelle: 'Panama', nomSource: 'Panama' },
  { code: 'PG', libelle: 'Papouasie-Nouvelle-Guinée', nomSource: 'Papua New Guinea' },
  { code: 'PY', libelle: 'Paraguay', nomSource: 'Paraguay' },
  { code: 'NL', libelle: 'Pays-Bas', nomSource: 'Netherlands' },
  { code: 'BQ', libelle: 'Pays-Bas caribéens', nomSource: 'Caribbean Netherlands' },
  { code: 'PE', libelle: 'Pérou', nomSource: 'Peru' },
  { code: 'PH', libelle: 'Philippines', nomSource: 'Philippines' },
  { code: 'PL', libelle: 'Pologne', nomSource: 'Poland' },
  { code: 'PF', libelle: 'Polynésie française', nomSource: 'French Polynesia' },
  { code: 'PR', libelle: 'Porto Rico', nomSource: 'Puerto Rico' },
  { code: 'PT', libelle: 'Portugal', nomSource: 'Portugal' },
  { code: 'QA', libelle: 'Qatar', nomSource: 'Qatar' },
  { code: 'HK', libelle: 'R.A.S. chinoise de Hong Kong', nomSource: 'Hong Kong SAR China' },
  { code: 'MO', libelle: 'R.A.S. chinoise de Macao', nomSource: 'Macao SAR China' },
  { code: 'CF', libelle: 'République centrafricaine', nomSource: 'Central African Republic' },
  { code: 'DO', libelle: 'République dominicaine', nomSource: 'Dominican Republic' },
  { code: 'RO', libelle: 'Roumanie', nomSource: 'Romania' },
  { code: 'GB', libelle: 'Royaume-Uni', nomSource: 'United Kingdom' },
  { code: 'RU', libelle: 'Russie', nomSource: 'Russia' },
  { code: 'RW', libelle: 'Rwanda', nomSource: 'Rwanda' },
  { code: 'EH', libelle: 'Sahara occidental', nomSource: 'Western Sahara' },
  { code: 'BL', libelle: 'Saint-Barthélemy', nomSource: 'St. Barthélemy' },
  { code: 'KN', libelle: 'Saint-Christophe-et-Niévès', nomSource: 'St. Kitts and Nevis' },
  { code: 'SM', libelle: 'Saint-Marin', nomSource: 'San Marino' },
  { code: 'MF', libelle: 'Saint-Martin', nomSource: 'St. Martin' },
  { code: 'SX', libelle: 'Saint-Martin (partie néerlandaise)', nomSource: 'Sint Maarten' },
  { code: 'PM', libelle: 'Saint-Pierre-et-Miquelon', nomSource: 'St. Pierre and Miquelon' },
  {
    code: 'VC',
    libelle: 'Saint-Vincent-et-les Grenadines',
    nomSource: 'St. Vincent and Grenadines',
  },
  { code: 'SH', libelle: 'Sainte-Hélène', nomSource: 'St. Helena' },
  { code: 'LC', libelle: 'Sainte-Lucie', nomSource: 'St. Lucia' },
  { code: 'SV', libelle: 'Salvador', nomSource: 'El Salvador' },
  { code: 'WS', libelle: 'Samoa', nomSource: 'Samoa' },
  { code: 'AS', libelle: 'Samoa américaines', nomSource: 'American Samoa' },
  { code: 'ST', libelle: 'Sao Tomé-et-Principe', nomSource: 'São Tomé and Príncipe' },
  { code: 'SN', libelle: 'Sénégal', nomSource: 'Senegal' },
  { code: 'RS', libelle: 'Serbie', nomSource: 'Serbia' },
  { code: 'SC', libelle: 'Seychelles', nomSource: 'Seychelles' },
  { code: 'SL', libelle: 'Sierra Leone', nomSource: 'Sierra Leone' },
  { code: 'SG', libelle: 'Singapour', nomSource: 'Singapore' },
  { code: 'SK', libelle: 'Slovaquie', nomSource: 'Slovakia' },
  { code: 'SI', libelle: 'Slovénie', nomSource: 'Slovenia' },
  { code: 'SO', libelle: 'Somalie', nomSource: 'Somalia' },
  { code: 'SD', libelle: 'Soudan', nomSource: 'Sudan' },
  { code: 'SS', libelle: 'Soudan du Sud', nomSource: 'South Sudan' },
  { code: 'LK', libelle: 'Sri Lanka', nomSource: 'Sri Lanka' },
  { code: 'SE', libelle: 'Suède', nomSource: 'Sweden' },
  { code: 'CH', libelle: 'Suisse', nomSource: 'Switzerland' },
  { code: 'SR', libelle: 'Suriname', nomSource: 'Suriname' },
  { code: 'SJ', libelle: 'Svalbard et Jan Mayen', nomSource: 'Svalbard and Jan Mayen' },
  { code: 'SY', libelle: 'Syrie', nomSource: 'Syria' },
  { code: 'TJ', libelle: 'Tadjikistan', nomSource: 'Tajikistan' },
  { code: 'TW', libelle: 'Taïwan', nomSource: 'Taiwan' },
  { code: 'TZ', libelle: 'Tanzanie', nomSource: 'Tanzania' },
  { code: 'TD', libelle: 'Tchad', nomSource: 'Chad' },
  { code: 'CZ', libelle: 'Tchéquie', nomSource: 'Czechia' },
  { code: 'TF', libelle: 'Terres australes françaises', nomSource: 'French Southern Territories' },
  {
    code: 'IO',
    libelle: 'Territoire britannique de l’océan Indien',
    nomSource: 'British Indian Ocean Territory',
  },
  { code: 'PS', libelle: 'Territoires palestiniens', nomSource: 'Palestinian Territories' },
  { code: 'TH', libelle: 'Thaïlande', nomSource: 'Thailand' },
  { code: 'TL', libelle: 'Timor oriental', nomSource: 'Timor-Leste' },
  { code: 'TG', libelle: 'Togo', nomSource: 'Togo' },
  { code: 'TK', libelle: 'Tokelau', nomSource: 'Tokelau' },
  { code: 'TO', libelle: 'Tonga', nomSource: 'Tonga' },
  { code: 'TT', libelle: 'Trinité-et-Tobago', nomSource: 'Trinidad and Tobago' },
  { code: 'TN', libelle: 'Tunisie', nomSource: 'Tunisia' },
  { code: 'TM', libelle: 'Turkménistan', nomSource: 'Turkmenistan' },
  { code: 'TR', libelle: 'Turquie', nomSource: 'Türkiye' },
  { code: 'TV', libelle: 'Tuvalu', nomSource: 'Tuvalu' },
  { code: 'UA', libelle: 'Ukraine', nomSource: 'Ukraine' },
  { code: 'UY', libelle: 'Uruguay', nomSource: 'Uruguay' },
  { code: 'VU', libelle: 'Vanuatu', nomSource: 'Vanuatu' },
  { code: 'VE', libelle: 'Venezuela', nomSource: 'Venezuela' },
  { code: 'VN', libelle: 'Viêt Nam', nomSource: 'Vietnam' },
  { code: 'WF', libelle: 'Wallis-et-Futuna', nomSource: 'Wallis and Futuna' },
  { code: 'YE', libelle: 'Yémen', nomSource: 'Yemen' },
  { code: 'ZM', libelle: 'Zambie', nomSource: 'Zambia' },
  { code: 'ZW', libelle: 'Zimbabwe', nomSource: 'Zimbabwe' },
];

// ── Familles d'artisanat : libellés et mots-clés de collecte ───────────────

// ── Ventes ───────────────────────────────────────────────────────────────────

/**
 * Périodes de lecture de la page Ventes. Chacune se compare à la même fenêtre
 * un an plus tôt, ce qui neutralise la saisonnalité ; le cumul n'a pas de
 * précédent.
 */
export const PERIODES_VENTES = ['90j', '12m', 'annee', 'tout'] as const;
export type PeriodeVentes = (typeof PERIODES_VENTES)[number];
export const PERIODE_VENTES_DEFAUT: PeriodeVentes = '12m';

export const PERIODE_VENTES_LABELS: Record<PeriodeVentes, string> = {
  '90j': '90 jours',
  '12m': '12 mois',
  annee: 'Année en cours',
  tout: 'Depuis le début',
};

/**
 * Période lue : un préréglage, ou `dates` quand des bornes au jour (`du`,
 * `au`, AAAA-MM-JJ, incluses) le remplacent. Une fenêtre choisie se compare,
 * elle aussi, à la même fenêtre un an plus tôt.
 */
export type PeriodeLue = PeriodeVentes | 'dates';

/**
 * Devises proposées à l'affichage. Les prix sont relevés en euros (devise de
 * référence) ; les autres s'obtiennent au taux saisi par l'administrateur.
 */
export const DEVISE_REFERENCE = 'EUR';
export const DEVISES_AFFICHAGE = ['EUR', 'MAD', 'USD', 'GBP', 'CAD', 'AUD', 'CHF'] as const;
export type DeviseAffichage = (typeof DEVISES_AFFICHAGE)[number];
export const DEVISE_LABELS: Record<DeviseAffichage, string> = {
  EUR: 'Euro',
  MAD: 'Dirham marocain',
  USD: 'Dollar américain',
  GBP: 'Livre sterling',
  CAD: 'Dollar canadien',
  AUD: 'Dollar australien',
  CHF: 'Franc suisse',
};

/** Date au jour, AAAA-MM-JJ. */
export const DATE_JOUR = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** Premier jour accepté pour une période choisie : avant, aucune vente n'est mesurée. */
export const PREMIER_JOUR_VENTES = '2005-01-01';

/**
 * Origine d'un article vendu : suivi par le catalogue, ou seulement cité par
 * un avis sans avoir été collecté.
 */
export const ORIGINES_VENTE = ['catalogue', 'hors_catalogue'] as const;
export type OrigineVente = (typeof ORIGINES_VENTE)[number];

// ── Avis ─────────────────────────────────────────────────────────────────────

/**
 * Motif d'un avis à 1 ou 2 étoiles. Le calcul, par mots-clés côté API, retient
 * le premier motif reconnu dans cet ordre ; « autre » et « sans texte » ferment
 * toujours la liste.
 */
export const MOTIFS_AVIS = [
  'non_recu',
  'livraison',
  'conformite',
  'numerique',
  'service',
  'autre',
  'sans_texte',
] as const;
export type MotifAvis = (typeof MOTIFS_AVIS)[number];

export const MOTIF_AVIS_LABELS: Record<MotifAvis, string> = {
  non_recu: 'Colis non reçu',
  livraison: 'Livraison, délais',
  conformite: 'Conformité, qualité',
  numerique: 'Fichier numérique',
  service: 'Service vendeur',
  autre: 'Autre',
  sans_texte: 'Sans texte',
};

/**
 * Thèmes relevés dans le texte des avis. Un thème n'est ni bon ni mauvais en
 * soi : il compte comme un compliment dans un avis à 4 ou 5 étoiles, comme un
 * reproche dans un avis à 1 ou 2. « No weird smells » parle d'odeur dans un
 * avis à cinq étoiles.
 */
export const THEMES_AVIS = [
  'couleur',
  'toucher',
  'photo',
  'dimensions',
  'qualite',
  'odeur',
  'livraison',
  'non_recu',
  'douane',
  'emballage',
  'vendeur',
  'prix',
  'cadeau',
] as const;
export type ThemeAvis = (typeof THEMES_AVIS)[number];

/**
 * Ce dont parle un thème : le produit lui-même, ce qu'un conseiller peut
 * rapporter aux artisans, ou l'achat (vendeur, livraison, prix).
 */
export const GROUPES_THEMES_AVIS = ['produit', 'achat'] as const;
export type GroupeThemeAvis = (typeof GROUPES_THEMES_AVIS)[number];

export const GROUPE_THEME_AVIS_LABELS: Record<GroupeThemeAvis, string> = {
  produit: 'Le produit',
  achat: "L'achat et la livraison",
};

export interface LibellesThemeAvis {
  /** Libellé du thème parmi les compliments. */
  positif: string;
  /** Libellé du thème parmi les reproches. */
  negatif: string;
  groupe: GroupeThemeAvis;
  /** Côté auquel le thème est réservé, quand l'autre n'aurait pas de sens. */
  cote?: 'positif' | 'negatif';
}

export const THEME_AVIS_LABELS: Record<ThemeAvis, LibellesThemeAvis> = {
  couleur: { positif: 'Couleurs', negatif: 'Couleur', groupe: 'produit' },
  toucher: { positif: 'Douceur, épaisseur', negatif: 'Toucher, épaisseur', groupe: 'produit' },
  photo: { positif: 'Fidèle à la photo', negatif: 'Écart à la photo', groupe: 'produit' },
  dimensions: { positif: 'Dimensions', negatif: 'Dimensions', groupe: 'produit' },
  qualite: { positif: 'Qualité, fabrication', negatif: 'Qualité, finition', groupe: 'produit' },
  odeur: { positif: 'Odeur', negatif: 'Odeur', groupe: 'produit' },
  livraison: { positif: 'Livraison', negatif: 'Délais de livraison', groupe: 'achat' },
  non_recu: {
    positif: 'Colis non reçu',
    negatif: 'Colis non reçu',
    groupe: 'achat',
    cote: 'negatif',
  },
  douane: {
    positif: 'Douane',
    negatif: 'Douane, frais d’importation',
    groupe: 'achat',
    cote: 'negatif',
  },
  emballage: { positif: 'Emballage', negatif: 'Emballage', groupe: 'achat' },
  vendeur: { positif: 'Échanges avec le vendeur', negatif: 'Service vendeur', groupe: 'achat' },
  prix: { positif: 'Rapport qualité-prix', negatif: 'Prix', groupe: 'achat' },
  cadeau: { positif: 'Cadeau réussi', negatif: 'Cadeau', groupe: 'achat', cote: 'positif' },
};

export const FAMILLE_LABELS: Record<FamilleArtisanat, string> = {
  [FamilleArtisanat.TAPIS]: 'Tapis',
  [FamilleArtisanat.POTERIE]: 'Poterie et céramique',
  [FamilleArtisanat.MAROQUINERIE]: 'Maroquinerie',
  [FamilleArtisanat.TEXTILE]: 'Textile',
  [FamilleArtisanat.VANNERIE]: 'Vannerie',
  [FamilleArtisanat.BOIS]: 'Bois et marqueterie',
  [FamilleArtisanat.METAL]: 'Métal et luminaire',
  [FamilleArtisanat.BIJOUX]: 'Bijoux',
  [FamilleArtisanat.COSMETIQUE]: 'Cosmétique et argan',
  [FamilleArtisanat.VETEMENT]: 'Vêtement traditionnel',
  [FamilleArtisanat.AUTRE]: 'Autre',
};

/**
 * Vocabulaire de recherche par famille. Sert à amorcer une configuration de
 * collecte : chaque mot-clé devient une requête, et l'origine est conservée
 * sur l'article pour segmenter les analyses.
 */
export const FAMILLE_MOTS_CLES: Record<FamilleArtisanat, readonly string[]> = {
  [FamilleArtisanat.TAPIS]: [
    'moroccan rug',
    'beni ourain rug',
    'azilal rug',
    'boujad rug',
    'boucherouite rug',
    'berber rug',
    'kilim moroccan',
    'taznakht rug',
  ],
  [FamilleArtisanat.POTERIE]: [
    'moroccan pottery',
    'tamegroute pottery',
    'tagine ceramic morocco',
    'safi pottery',
    'zellige tile',
  ],
  [FamilleArtisanat.MAROQUINERIE]: [
    'moroccan leather bag',
    'babouche slippers',
    'moroccan pouf leather',
    'moroccan handbag leather',
  ],
  [FamilleArtisanat.TEXTILE]: [
    'moroccan blanket handira',
    'handira wedding blanket',
    'fouta towel morocco',
    'moroccan cushion cover',
    'sabra silk pillow',
  ],
  [FamilleArtisanat.VANNERIE]: [
    'moroccan basket straw',
    'moroccan bag straw',
    'palm leaf basket morocco',
  ],
  [FamilleArtisanat.BOIS]: ['thuya wood box', 'moroccan wooden box', 'moroccan carved wood'],
  [FamilleArtisanat.METAL]: [
    'moroccan lantern',
    'moroccan lamp brass',
    'moroccan tea tray',
    'moroccan teapot',
  ],
  [FamilleArtisanat.BIJOUX]: ['berber jewelry', 'moroccan silver necklace', 'amazigh jewelry'],
  [FamilleArtisanat.COSMETIQUE]: [
    'argan oil morocco',
    'moroccan ghassoul clay',
    'black soap morocco beldi',
  ],
  [FamilleArtisanat.VETEMENT]: ['moroccan kaftan', 'djellaba moroccan', 'moroccan burnous'],
  [FamilleArtisanat.AUTRE]: [],
};
